import React, {useState} from 'react';
import {Box, Button, Card, Container, Link as MuiLink, TextField, Typography} from '@mui/material';
import {Link} from 'react-router-dom';
import axios from 'axios';
import Logo from '../core/components/Logo';
import {ThemedText} from '../core/components/ThemedText';

const FORGOT_PASSWORD_ENDPOINT = '/auth/forgot-password';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [resetToken, setResetToken] = useState('');

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');
        setSubmitting(true);

        try {
            const client = axios.create({
                baseURL: process.env.REACT_APP_API_URL,
            });

            const { data } = await client.post(FORGOT_PASSWORD_ENDPOINT, { email });
            setSuccessMessage('If an account exists for this email, a reset link has been sent.');
            setResetToken(data?.resetToken || '');
        } catch (error) {
            setErrorMessage('Unable to send reset link. Please try again.');
            setResetToken('');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container maxWidth="xs">
            <Box
                sx={{
                    marginTop: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Card sx={{ p: 3, width: '100%' }}>
                    <Logo />
                    <ThemedText style={{ marginTop: '20px', marginBottom: '20px'}} align="center" type="title">
                        Reset password
                    </ThemedText>
                    <ThemedText style={{ marginBottom: '20px' }} align="center" type="subtitle">
                        Enter your email and we will send you a reset link.
                    </ThemedText>
                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                        {errorMessage && (
                            <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
                                {errorMessage}
                            </Typography>
                        )}
                        {successMessage && (
                            <Typography variant="body2" color="success.main" align="center" sx={{ mb: 2 }}>
                                {successMessage}
                            </Typography>
                        )}
                        {resetToken && (
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Dev token:
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    {resetToken}
                                </Typography>
                                <Link to={`/reset-password?token=${resetToken}`} style={{ textDecoration: 'none' }}>
                                    <MuiLink variant="body2" style={{ textDecoration: 'none' }}>
                                        Open reset page
                                    </MuiLink>
                                </Link>
                            </Box>
                        )}
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px'}}>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={submitting}
                                sx={{ mt: 3, mb: 2 }}
                            >
                                {submitting ? 'Sending...' : 'Send reset link'}
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Link to="/login" style={{ textDecoration: 'none'}}>
                                <MuiLink variant="body2" style={{ textDecoration: 'none'}}>
                                    Back to sign in
                                </MuiLink>
                            </Link>
                        </Box>
                    </Box>
                </Card>
            </Box>
        </Container>
    );
};

export default ForgotPassword;
